import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import {
  API,
  authHeaders,
  btnPrimary,
  btnSecondary,
  fieldStyle,
  labelStyle,
  alertError,
  downloadJson,
} from '../api/ticketsApi';
import { ModuleShell } from '../../../components/module-ui';
import { Database } from '@phosphor-icons/react';
import DataSourceEditorForm from '../components/DataSourceEditorForm';

import { apiPath } from '../../../config/apiBase';
import { notifySuccess, notifyError } from '../../../lib/notify';
const DS = `${API}/data-sources`;
const CONN = `${DS}/connections`;
const PRODUCTS_API = apiPath('/api/products');

const blankConn = () => ({ name: '', base_url: '', auth_type: 'none', token: '', api_key_header: '', api_key_value: '', username: '', password: '' });
const blankSource = () => ({
  name: '', product_id: '', type: 'static', connection_id: '', url_template: '', method: 'GET',
  array_path: '', value_key: 'value', label_key: 'label', static_options: [], static_groups: {},
  _staticOptions: [], _staticMode: 'flat', _groups: [],
});

export default function DataSourcesPage() {
  const [tab, setTab] = useState('sources');
  const [connections, setConnections] = useState([]);
  const [sources, setSources] = useState([]);
  const [products, setProducts] = useState([]);
  const [productFilter, setProductFilter] = useState('all');
  const [connForm, setConnForm] = useState(null);
  const [srcForm, setSrcForm] = useState(null);
  const [savingSrc, setSavingSrc] = useState(false);
  const [preview, setPreview] = useState({ id: null, params: '{}', result: null, err: null });
  const [err, setErr] = useState(null);
  const importRef = useRef(null);
  const singleImportRef = useRef(null);
  const [singleImportTarget, setSingleImportTarget] = useState(null);

  const load = async () => {
    try {
      const [c, s] = await Promise.all([
        axios.get(CONN, authHeaders()), axios.get(DS, authHeaders()),
      ]);
      setConnections(c.data || []); setSources(s.data || []);
    } catch (e) { setErr('Failed to load'); }
    try {
      const p = await axios.get(PRODUCTS_API, authHeaders());
      setProducts(p.data || []);
    } catch (e) { /* keep existing products */ }
  };
  useEffect(() => { load(); }, []);

  const productName = (pid) => products.find((p) => p.product_id === pid)?.name;
  const countFor = (pid) => sources.filter(
    (s) => (pid === 'shared' ? !s.product_id : s.product_id === pid)).length;
  const visibleSources = sources.filter((s) => {
    if (productFilter === 'all') return true;
    if (productFilter === 'shared') return !s.product_id;
    return s.product_id === productFilter;
  });
  const flash = (m) => { notifySuccess(m); };

  const saveConn = async () => {
    try {
      if (connForm.connection_id) await axios.put(`${CONN}/${connForm.connection_id}`, connForm, authHeaders());
      else await axios.post(CONN, connForm, authHeaders());
      setConnForm(null); flash('Connection saved'); load();
    } catch (e) { setErr(e.response?.data?.detail || 'Save failed'); }
  };
  const delConn = async (id) => {
    if (window.confirm('Delete connection?')) {
      await axios.delete(`${CONN}/${id}`, authHeaders());
      load();
    }
  };

  const saveSrc = async () => {
    if (!srcForm?.name?.trim()) {
      notifyError('Name is required');
      return;
    }
    setSavingSrc(true);
    setErr(null);
    try {
      const { _staticOptions, _staticMode, _groups, ...rest } = srcForm;
      const payload = {
        ...rest,
        name: rest.name.trim(),
        product_id: rest.product_id || null,
        static_options: [],
        static_groups: {},
        group_key_fields: [],
        source_kind: null,
      };
      if (rest.type === 'static') {
        if (_staticMode === 'branch_reporters') {
          const sg = {};
          (_groups || []).forEach((g) => {
            const k = (g.key || '').trim();
            if (!k) return;
            sg[k] = (g.options || [])
              .filter((o) => (o.value || '').trim())
              .map((o) => ({
                value: o.value.trim(),
                label: (o.label || o.value).trim(),
                contact_no: (o.contact_no || '').trim() || undefined,
                email: (o.email || '').trim() || undefined,
              }));
          });
          payload.static_groups = sg;
          payload.group_key_fields = ['group', 'company', 'branch'];
          payload.source_kind = 'branch_reporters';
        } else if (_staticMode === 'grouped') {
          const sg = {};
          (_groups || []).forEach((g) => {
            const k = (g.key || '').trim();
            if (!k) return;
            sg[k] = (g.options || [])
              .filter((o) => (o.value || '').trim())
              .map((o) => ({
                value: o.value.trim(),
                label: (o.label || o.value).trim(),
              }));
          });
          payload.static_groups = sg;
        } else {
          payload.static_options = (_staticOptions || [])
            .filter((o) => (o.value || '').trim())
            .map((o) => ({
              value: o.value.trim(),
              label: (o.label || o.value).trim(),
            }));
        }
      }
      if (payload.data_source_id) await axios.put(`${DS}/${payload.data_source_id}`, payload, authHeaders());
      else await axios.post(DS, payload, authHeaders());
      setSrcForm(null);
      flash('Data source saved');
      load();
    } catch (e) {
      const detail = e.response?.data?.detail || 'Save failed';
      setErr(detail);
      notifyError(typeof detail === 'string' ? detail : 'Save failed');
    } finally {
      setSavingSrc(false);
    }
  };
  const delSrc = async (id) => {
    if (window.confirm('Delete data source?')) {
      await axios.delete(`${DS}/${id}`, authHeaders());
      load();
    }
  };

  const openEdit = (s) => {
    const grouped = s.static_groups && Object.keys(s.static_groups).length > 0;
    const isBranchReporters = s.source_kind === 'branch_reporters'
      || (s.group_key_fields || []).join(',') === 'group,company,branch';
    setSrcForm({
      ...blankSource(),
      ...s,
      _staticOptions: (s.static_options || []).map((o) => ({
        value: o.value || '',
        label: o.label || o.value || '',
      })),
      _staticMode: isBranchReporters ? 'branch_reporters' : (grouped ? 'grouped' : 'flat'),
      _groups: grouped
        ? Object.entries(s.static_groups).map(([key, opts]) => ({
          key,
          options: (opts || []).map((o) => ({
            value: o.value || '',
            label: o.label || o.value || '',
            contact_no: o.contact_no || '',
            email: o.email || '',
          })),
        }))
        : [],
    });
    setErr(null);
  };

  const scopeParam = () => {
    if (productFilter === 'all') return '';
    if (productFilter === 'shared') return '?product_id=__shared__';
    return `?product_id=${productFilter}`;
  };
  const scopeLabel = () => {
    if (productFilter === 'all') return 'all';
    if (productFilter === 'shared') return 'shared';
    return productName(productFilter) || productFilter;
  };
  const exportSources = async () => {
    setErr(null);
    try {
      const r = await axios.get(`${DS}/export${scopeParam()}`, authHeaders());
      const stamp = new Date().toISOString().slice(0, 10);
      downloadJson(`data-sources_${scopeLabel()}_${stamp}.json`, r.data);
      flash(`Exported ${r.data.count || 0} data source(s)`);
    } catch { setErr('Export failed'); }
  };
  const importSources = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (productFilter === 'all') {
      setErr('Select a product (or Shared) before importing — import copies into the current filter.');
      return;
    }
    setErr(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const sep = scopeParam().includes('?') ? '&' : '?';
      const r = await axios.post(`${DS}/import${scopeParam()}${sep}mode=copy`, fd, authHeaders());
      const d = r.data || {};
      flash(`Imported: ${d.created || 0} new, ${d.updated || 0} updated${d.connections_imported ? `, ${d.connections_imported} connection(s)` : ''}`);
      load();
    } catch (e2) { setErr(e2.response?.data?.detail || 'Import failed'); }
  };

  const exportOneSource = async (s) => {
    try {
      const r = await axios.get(`${DS}/export?product_id=${s.product_id || '__shared__'}`, authHeaders());
      const one = {
        ...r.data,
        count: 1,
        data_sources: (r.data.data_sources || []).filter((x) => x.data_source_id === s.data_source_id),
      };
      downloadJson(`data-source_${s.data_source_id}.json`, one);
      flash('Exported 1 data source');
    } catch { setErr('Export failed'); }
  };

  const openSingleImport = (s) => {
    setSingleImportTarget({ product_id: s.product_id || '__shared__' });
    setTimeout(() => singleImportRef.current?.click(), 0);
  };
  const importOneSource = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    const target = singleImportTarget;
    setSingleImportTarget(null);
    if (!file || !target) return;
    setErr(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const pid = target.product_id === '__shared__' ? '__shared__' : target.product_id;
      const r = await axios.post(`${DS}/import?product_id=${pid}&mode=copy`, fd, authHeaders());
      const d = r.data || {};
      flash(`Imported: ${d.created || 0} new, ${d.updated || 0} updated${d.connections_imported ? `, ${d.connections_imported} connection(s)` : ''}`);
      load();
    } catch (e2) {
      setErr(e2.response?.data?.detail || 'Import failed');
    }
  };

  const exportExcelOne = async (s) => {
    if (s.type !== 'static') {
      notifyError('Excel export is only for static data sources');
      return;
    }
    try {
      const r = await axios.get(`${DS}/${s.data_source_id}/excel/export`, {
        ...authHeaders(),
        responseType: 'blob',
      });
      const blob = new Blob([r.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${s.name || s.data_source_id}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      flash('Excel exported');
    } catch (e) {
      notifyError(e.response?.data?.detail || 'Excel export failed');
    }
  };

  const runPreview = async (id) => {
    setPreview((p) => ({ ...p, id, result: null, err: null }));
    let params = {};
    try { params = JSON.parse(preview.params || '{}'); } catch { setPreview((p) => ({ ...p, err: 'Params must be valid JSON' })); return; }
    try {
      const r = await axios.post(`${DS}/resolve`, { data_source_id: id, params }, authHeaders());
      setPreview((p) => ({ ...p, result: r.data, err: null }));
    } catch (e) { setPreview((p) => ({ ...p, err: e.response?.data?.detail || 'Resolve failed' })); }
  };

  return (
    <ModuleShell
      title="Data Sources"
      subtitle="Register API connections and data sources once — form dropdowns bind to them for dynamic and cascading options."
      icon={Database}
      subnav={(
        <nav className="tk-subnav" aria-label="Data sources navigation">
          <button
            type="button"
            className={`tk-subnav-link${tab === 'sources' ? ' is-active' : ''}`}
            onClick={() => { setTab('sources'); setSrcForm(null); }}
          >
            <span>Data Sources</span>
          </button>
          <button
            type="button"
            className={`tk-subnav-link${tab === 'connections' ? ' is-active' : ''}`}
            onClick={() => { setTab('connections'); setSrcForm(null); }}
          >
            <span>Connections</span>
          </button>
        </nav>
      )}
    >
    <div style={{ maxWidth: srcForm ? '1100px' : '980px' }}>
      {err && <div style={alertError}>{typeof err === 'string' ? err : JSON.stringify(err)}</div>}

      {tab === 'connections' && (
        <div>
          {!connForm && <button style={btnPrimary} onClick={() => setConnForm(blankConn())}>+ New Connection</button>}
          {connForm && (
            <div style={card}>
              <div style={grid2}>
                <div><label style={labelStyle}>Name *</label><input style={fieldStyle} value={connForm.name} onChange={(e) => setConnForm({ ...connForm, name: e.target.value })} /></div>
                <div><label style={labelStyle}>Base URL *</label><input style={fieldStyle} value={connForm.base_url} placeholder="https://esahaya.example.com/api" onChange={(e) => setConnForm({ ...connForm, base_url: e.target.value })} /></div>
                <div><label style={labelStyle}>Auth</label>
                  <select style={fieldStyle} value={connForm.auth_type} onChange={(e) => setConnForm({ ...connForm, auth_type: e.target.value })}>
                    <option value="none">None</option><option value="bearer">Bearer token</option>
                    <option value="api_key">API key header</option><option value="basic">Basic auth</option>
                  </select>
                </div>
                {connForm.auth_type === 'bearer' && <div><label style={labelStyle}>Token</label><input style={fieldStyle} value={connForm.token} onChange={(e) => setConnForm({ ...connForm, token: e.target.value })} /></div>}
                {connForm.auth_type === 'api_key' && <>
                  <div><label style={labelStyle}>Header name</label><input style={fieldStyle} value={connForm.api_key_header} placeholder="X-API-Key" onChange={(e) => setConnForm({ ...connForm, api_key_header: e.target.value })} /></div>
                  <div><label style={labelStyle}>Header value</label><input style={fieldStyle} value={connForm.api_key_value} onChange={(e) => setConnForm({ ...connForm, api_key_value: e.target.value })} /></div>
                </>}
                {connForm.auth_type === 'basic' && <>
                  <div><label style={labelStyle}>Username</label><input style={fieldStyle} value={connForm.username} onChange={(e) => setConnForm({ ...connForm, username: e.target.value })} /></div>
                  <div><label style={labelStyle}>Password</label><input style={fieldStyle} type="password" value={connForm.password} onChange={(e) => setConnForm({ ...connForm, password: e.target.value })} /></div>
                </>}
              </div>
              <div style={{ display: 'flex', gap: '8px', marginTop: '12px' }}>
                <button style={btnPrimary} onClick={saveConn}>Save</button>
                <button style={btnSecondary} onClick={() => setConnForm(null)}>Cancel</button>
              </div>
            </div>
          )}
          <div style={{ display: 'grid', gap: '8px', marginTop: '14px' }}>
            {connections.map((c) => (
              <div key={c.connection_id} className="ds-row">
                <div><b>{c.name}</b> <span style={{ color: '#6B7280', fontSize: '12px' }}>· {c.base_url} · {c.auth_type}</span></div>
                <div className="ds-row-actions">
                  <button style={btnSecondary} onClick={() => setConnForm({ ...blankConn(), ...c })}>Edit</button>
                  <button style={{ ...btnSecondary, color: '#DC2626' }} onClick={() => delConn(c.connection_id)}>Delete</button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'sources' && (
        <div>
          {!srcForm && (
            <>
              <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', paddingBottom: '6px', marginBottom: '8px' }}>
                <ProductCard label="All" count={sources.length} active={productFilter === 'all'} onClick={() => setProductFilter('all')} />
                {products.map((p) => (
                  <ProductCard
                    key={p.product_id}
                    label={p.name}
                    count={countFor(p.product_id)}
                    active={productFilter === p.product_id}
                    onClick={() => setProductFilter(p.product_id)}
                  />
                ))}
                <ProductCard label="Shared / General" count={countFor('shared')} active={productFilter === 'shared'} onClick={() => setProductFilter('shared')} />
              </div>

              {productFilter !== 'all' && (
                <p style={{ fontSize: '12px', color: '#6B7280', margin: '0 0 12px' }}>
                  Showing data sources for <b>{productFilter === 'shared' ? 'Shared / General' : (productName(productFilter) || productFilter)}</b>
                  {' · '}{visibleSources.length} source(s)
                </p>
              )}

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
                <button
                  style={btnPrimary}
                  onClick={() => setSrcForm({
                    ...blankSource(),
                    product_id: (productFilter !== 'all' && productFilter !== 'shared') ? productFilter : '',
                  })}
                >
                  + New Data Source
                </button>
                <button style={btnSecondary} onClick={exportSources} disabled={!visibleSources.length}
                  title={`Download ${scopeLabel()} data sources as JSON`}>Export all (JSON)</button>
                <button style={btnSecondary} onClick={() => importRef.current?.click()}
                  title="Copy data sources from a JSON export into this product">Import / Copy (JSON)</button>
              </div>
              <input ref={importRef} type="file" accept="application/json,.json" style={{ display: 'none' }} onChange={importSources} />
              <input ref={singleImportRef} type="file" accept="application/json,.json" style={{ display: 'none' }} onChange={importOneSource} />

              <div style={{ display: 'grid', gap: '10px', marginTop: '14px' }}>
                {visibleSources.length === 0 && (
                  <div className="ds-row" style={{ justifyContent: 'center', color: '#9CA3AF' }}>
                    No data sources {productFilter === 'all' ? 'yet' : 'for this product'}.
                  </div>
                )}
                {visibleSources.map((s) => (
                  <div key={s.data_source_id} style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    <div className="ds-row" style={{ alignItems: 'flex-start' }}>
                      <div style={{ minWidth: 0, flex: 1 }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}>
                          <b>{s.name}</b>
                          <span style={pill}>{s.product_id ? (productName(s.product_id) || s.product_id) : 'Shared'}</span>
                        </div>
                        <div style={{ color: '#6B7280', fontSize: '12px', marginTop: 2 }}>
                          {s.type}
                          {s.source_kind === 'branch_reporters' ? ' · branch reporters' : ''}
                          {s.type === 'static' && s.static_groups && Object.keys(s.static_groups).length
                            ? ` · ${Object.keys(s.static_groups).length} groups / ${Object.values(s.static_groups).reduce((n, a) => n + (a?.length || 0), 0)} options`
                            : ''}
                          {s.type === 'static' && !(s.static_groups && Object.keys(s.static_groups).length)
                            ? ` · ${(s.static_options || []).length} options`
                            : ''}
                          {s.type === 'rest' ? ` · ${s.url_template}` : ''}
                        </div>
                        <div style={{ fontFamily: 'monospace', fontSize: '11px', color: '#9CA3AF', marginTop: 2 }}>{s.data_source_id}</div>
                      </div>
                      <div className="ds-row-actions">
                        <button style={btnSecondary} onClick={() => runPreview(s.data_source_id)}>Test</button>
                        <button style={btnSecondary} onClick={() => openEdit(s)}>Edit</button>
                        {s.type === 'static' && (
                          <button type="button" style={btnSecondary} onClick={() => exportExcelOne(s)} title="Export options as Excel">
                            Excel
                          </button>
                        )}
                        <button type="button" style={btnSecondary} onClick={() => exportOneSource(s)} title="Export this data source as JSON">
                          Export
                        </button>
                        <button type="button" style={btnSecondary} onClick={() => openSingleImport(s)} title="Import a JSON file into this product">
                          Import
                        </button>
                        <button style={{ ...btnSecondary, color: '#DC2626' }} onClick={() => delSrc(s.data_source_id)}>Delete</button>
                      </div>
                    </div>
                    {preview.id === s.data_source_id && (
                      <div style={{ background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: '12px', padding: '10px' }}>
                        <label style={labelStyle}>Test params (JSON, e.g. {'{"state":"KA"}'})</label>
                        <div style={{ display: 'flex', gap: '8px' }}>
                          <input style={fieldStyle} value={preview.params} onChange={(e) => setPreview({ ...preview, params: e.target.value })} />
                          <button style={btnSecondary} onClick={() => runPreview(s.data_source_id)}>Run</button>
                        </div>
                        {preview.err && <div style={{ color: '#DC2626', fontSize: '12px', marginTop: '6px' }}>{preview.err}</div>}
                        {preview.result && <div style={{ fontSize: '12px', marginTop: '6px' }}>{preview.result.length} option(s): {preview.result.slice(0, 8).map((o) => o.label).join(', ')}{preview.result.length > 8 ? '…' : ''}</div>}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </>
          )}

          {srcForm && (
            <DataSourceEditorForm
              form={srcForm}
              setForm={setSrcForm}
              products={products}
              connections={connections}
              onSave={saveSrc}
              onCancel={() => setSrcForm(null)}
              saving={savingSrc}
            />
          )}
        </div>
      )}
    </div>
    </ModuleShell>
  );
}

function ProductCard({ label, count, active, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`ds-product-chip${active ? ' is-active' : ''}`}
    >
      <div style={{ fontSize: '12px', fontWeight: 600, color: '#111827' }}>{label}</div>
      <div style={{ fontSize: '12px', color: '#6B7280', marginTop: '2px' }}>{count} source{count === 1 ? '' : 's'}</div>
    </button>
  );
}

const card = { border: '1px solid #E5E7EB', borderRadius: '12px', padding: '12px', marginTop: '12px', background: 'white' };
const grid2 = { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' };
const pill = { display: 'inline-block', marginLeft: '4px', padding: '2px 8px', borderRadius: '999px', background: '#EFF6FF', color: '#1D4ED8', fontSize: '11px', fontWeight: 600 };
